document.addEventListener('alpine:init', () => {
  Alpine.data('ctrAbGallery', () => ({
    config: null,
    sectionEl: null,
    abGroup: 'control',
    activeGroupKey: null,
    igReady: false,
    wasAbActive: false,
    pendingOriginal: false,
    pendingOriginalTimer: null,

    init() {
      const configEl = this.$el.querySelector('[data-ctr-ab-gallery-config]');
      if (!configEl?.textContent) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[ctr-ab-gallery] Failed to parse config', e);
        return;
      }

      if (!this.config?.groups || !Object.keys(this.config.groups).length) return;

      this.sectionEl = this.$el.closest('[data-section-type="product"]');
      this.bindEvents();
      this.resolveIgGroup(() => this.sync());
      this.$nextTick(() => this.sync());
    },

    getOriginalGallery() {
      if (!this.sectionEl) return null;
      return this.sectionEl.querySelector('[data-product-gallery]:not([data-ctr-ab-group])');
    },

    flavorKey(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    },

    getUrlOverride() {
      const v = new URL(window.location.href).searchParams.get('ab_gallery');
      if (!v) return null;
      const key = this.flavorKey(v);
      if (key === 'control' || key === this.flavorKey(this.config.controlName)) return 'control';
      if (this.config.groups[key]) return key;
      return null;
    },

    /**
     * Intelligems onsite tests can set: window.ctrAbGalleryGroup = 'control'|'a'|'b'|'c'
     */
    getGlobalGroup() {
      const raw = window.ctrAbGalleryGroup;
      if (raw == null || raw === '') return null;
      return this.normalizeIgGroup(raw);
    },

    normalizeIgGroup(raw) {
      if (raw == null) return 'control';
      const name = this.flavorKey(raw.name || raw.id || raw);
      if (!name) return 'control';
      if (name === this.flavorKey(this.config.controlName) || name.includes('control')) {
        return 'control';
      }

      for (const [key, group] of Object.entries(this.config.groups)) {
        const ig = this.flavorKey(group.igName || key);
        if (name === ig || name === key || name === `variant-${key}`) return key;
      }

      const last = name.split('-').pop();
      if (this.config.groups[last]) return last;
      return 'control';
    },

    /**
     * Resolution order:
     * 1. ?ab_gallery= URL override
     * 2. block setting gallery_version (theme editor force)
     * 3. window.ctrAbGalleryGroup (Intelligems onsite)
     * 4. igData.user.getTestGroup(experimentId) metafield fallback
     * 5. control
     */
    resolveIgGroup(done) {
      const override = this.getUrlOverride();
      if (override) {
        this.abGroup = override;
        this.igReady = true;
        done?.();
        return;
      }

      const finish = (group) => {
        this.abGroup = group || 'control';
        this.igReady = true;
        done?.();
      };

      const forcedVersion = String(this.config.galleryVersion || '').trim();
      if (forcedVersion) {
        finish(this.normalizeIgGroup(forcedVersion));
        return;
      }

      const tryResolve = () => {
        const fromGlobal = this.getGlobalGroup();
        if (fromGlobal) {
          finish(fromGlobal);
          return true;
        }

        if (this.config.experimentId && window.igData?.user?.getTestGroup) {
          try {
            const testGroup = window.igData.user.getTestGroup(this.config.experimentId);
            if (testGroup != null && testGroup !== '') {
              finish(this.normalizeIgGroup(testGroup));
              return true;
            }
          } catch (e) {
            console.error('[ctr-ab-gallery] Intelligems lookup failed', e);
          }
        }

        return false;
      };

      if (tryResolve()) {
        this.watchGlobalGroup();
        return;
      }

      const onIgReady = () => {
        if (!this.igReady) tryResolve();
      };
      window.addEventListener('ig:ready', onIgReady, { once: true });

      // Onsite snippets may set the global after paint without ig:ready
      let attempts = 0;
      const poll = window.setInterval(() => {
        attempts += 1;
        if (tryResolve() || attempts >= 30) {
          window.clearInterval(poll);
          if (!this.igReady) finish('control');
          this.watchGlobalGroup();
        }
      }, 100);
    },

    /** Re-sync if Intelligems sets/updates the onsite global later. */
    watchGlobalGroup() {
      if (this._globalWatchBound) return;
      this._globalWatchBound = true;

      let last = window.ctrAbGalleryGroup;
      window.setInterval(() => {
        if (this.getUrlOverride()) return;
        const next = window.ctrAbGalleryGroup;
        if (next === last) return;
        last = next;
        const group = this.getGlobalGroup();
        if (!group || group === this.abGroup) return;
        this.abGroup = group;
        this.igReady = true;
        this.sync();
      }, 500);
    },

    parseProductJson(el) {
      if (!el?.textContent) return null;
      try {
        const data = JSON.parse(el.textContent);
        return {
          variants: data.variants || data?.product?.variants || [],
          options: data.options || data?.product?.options || [],
        };
      } catch (e) {
        return null;
      }
    },

    getProductDatasets() {
      if (!this.sectionEl) return [];
      if (this._productDatasets) return this._productDatasets;

      const datasets = [];
      const main = this.parseProductJson(this.sectionEl.querySelector('[data-product-json]'));
      const sub = this.parseProductJson(this.sectionEl.querySelector('[data-sub-product-json]'));
      if (main) datasets.push(main);
      if (sub) datasets.push(sub);
      this._productDatasets = datasets;
      return datasets;
    },

    getVariant() {
      if (!this.sectionEl) return null;
      const id = this.sectionEl.querySelector('input[name="id"]')?.value;
      if (!id) return null;

      for (const dataset of this.getProductDatasets()) {
        const match = dataset.variants.find((v) => String(v.id) === String(id));
        if (match) return match;
      }
      return null;
    },

    getOptionValue(variant, index) {
      if (!variant || index == null || index < 0) return '';
      if (Array.isArray(variant.options) && variant.options[index] != null) {
        return String(variant.options[index]);
      }
      return String(variant[`option${index + 1}`] || '');
    },

    /** Prefer option name lookup so OTP + subscription products stay aligned. */
    getOptionValueByName(variant, names) {
      if (!variant || !names?.length) return '';

      const wanted = names.map((n) => this.flavorKey(n));
      for (const dataset of this.getProductDatasets()) {
        const options = dataset.options || [];
        for (let i = 0; i < options.length; i++) {
          const opt = options[i];
          const name = this.flavorKey(typeof opt === 'string' ? opt : opt?.name);
          if (wanted.includes(name)) {
            return this.getOptionValue(variant, i);
          }
        }
      }

      // Fallback to Liquid-provided indexes from the page product
      if (wanted.includes('type') && this.config?.typeIndex != null) {
        return this.getOptionValue(variant, this.config.typeIndex);
      }
      if ((wanted.includes('flavor') || wanted.includes('flavour')) && this.config?.flavorIndex != null) {
        return this.getOptionValue(variant, this.config.flavorIndex);
      }
      return '';
    },

    isBagVariant(variant) {
      if (!variant || !this.config) return false;
      const typeValue = this.getOptionValueByName(variant, ['type']) ||
        this.getOptionValue(variant, this.config.typeIndex);
      return this.flavorKey(typeValue) === this.flavorKey(this.config.bagValue);
    },

    getFlavorKey(variant) {
      const flavorValue = this.getOptionValueByName(variant, ['flavor', 'flavour']) ||
        this.getOptionValue(variant, this.config?.flavorIndex);
      return this.flavorKey(flavorValue);
    },

    setVisible(el, visible) {
      if (!el) return;
      if (visible) {
        el.hidden = false;
        el.style.display = el.hasAttribute('data-product-gallery') ? '' : 'block';
      } else {
        el.hidden = true;
        el.style.display = 'none';
      }
    },

    clearPendingOriginal() {
      this.pendingOriginal = false;
      if (this.pendingOriginalTimer) {
        clearTimeout(this.pendingOriginalTimer);
        this.pendingOriginalTimer = null;
      }
    },

    /**
     * PGV/ProductImages often leave --height: 0 after measuring a hidden gallery.
     * Seed a real height from the first decoded image before it enters the flow.
     */
    prepareOriginalGalleryHeight(original) {
      if (!original) return;

      const img =
        original.querySelector('.media--active img') ||
        original.querySelector('[data-image-id] img') ||
        original.querySelector('img');
      const hostWidth = this.$el.offsetWidth || this.$el.parentElement?.clientWidth || 0;
      if (img?.naturalWidth && hostWidth) {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        // Mobile slider slides are slightly narrower (peek of next slide).
        const slideWidth = isMobile ? Math.max(1, hostWidth - 50) : hostWidth;
        const height = slideWidth * (img.naturalHeight / img.naturalWidth);
        if (height > 0) {
          original.style.setProperty('--height', `${Math.round(height)}px`);
          return;
        }
      }

      const current = original.style.getPropertyValue('--height');
      if (!current || current === '0px') original.style.removeProperty('--height');
    },

    refreshOriginalGalleryHeight(original) {
      if (!original) return;

      const measure = () => {
        const active =
          original.querySelector('[data-image-id].media--active') ||
          original.querySelector('[data-image-id]');
        const h = active?.offsetHeight || 0;
        if (h > 0) original.style.setProperty('--height', `${h}px`);
      };

      measure();
      requestAnimationFrame(() => {
        measure();
        requestAnimationFrame(measure);
      });

      original.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', measure, { once: true });
      });
    },

    /**
     * zoom.js reads the first [data-pswp-thumbs-template] in the section.
     * Promote the active gallery's template so Photoswipe thumbs match.
     */
    setPswpTemplateActive(groupEl) {
      if (!this.sectionEl) return;

      const ATTR = 'data-pswp-thumbs-template';
      const INACTIVE = 'data-pswp-thumbs-template-inactive';

      this.sectionEl.querySelectorAll(`[${ATTR}], [${INACTIVE}]`).forEach((tpl) => {
        const inAb = tpl.hasAttribute('data-ctr-ab-pswp-template');
        const isActiveAb = groupEl && inAb && groupEl.contains(tpl);
        const shouldBeActive = groupEl ? isActiveAb : !inAb;

        if (shouldBeActive) {
          tpl.setAttribute(ATTR, '');
          tpl.removeAttribute(INACTIVE);
        } else {
          tpl.removeAttribute(ATTR);
          tpl.setAttribute(INACTIVE, '');
        }
      });
    },

    /**
     * ProductImages.setHeight() runs while AB instances are hidden → --height: 0.
     * Theme CSS then forces [data-fader-desktop] .product__slides { height: var(--height) }.
     * Remeasure after the gallery is visible.
     */
    refreshGalleryHeight(groupEl) {
      const host = groupEl?.querySelector('.ctr-ab-gallery__zoom-host');
      if (!host) return;

      // Clear stale 0px from hidden init so slides can size naturally until remasured
      const current = host.style.getPropertyValue('--height');
      if (!current || current === '0px') host.style.removeProperty('--height');

      const measure = () => {
        const active =
          host.querySelector('[data-image-id].media--active') ||
          host.querySelector('[data-image-id]');
        const h = active?.offsetHeight || 0;
        // Never write 0 — ProductImages may have already set --height: 0 while hidden
        if (h > 0) host.style.setProperty('--height', `${h}px`);
      };

      requestAnimationFrame(() => {
        measure();
        requestAnimationFrame(measure);
      });

      host.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', measure, { once: true });
      });
    },

    showOriginal({ galleryReady = false } = {}) {
      const leavingAb = this.wasAbActive || this.pendingOriginal;
      const original = this.getOriginalGallery();

      // Keep AB in normal flow until PGV has the travel-pack gallery ready.
      // Absolute covers caused mobile jumps by pulling AB out of layout.
      if (leavingAb && !galleryReady) {
        this.pendingOriginal = true;
        this.wasAbActive = false;
        this.setVisible(this.$el, true);
        this.setVisible(original, false);
        if (this.pendingOriginalTimer) clearTimeout(this.pendingOriginalTimer);
        this.pendingOriginalTimer = setTimeout(() => {
          this.pendingOriginalTimer = null;
          if (!this.pendingOriginal) return;
          this.showOriginal({ galleryReady: true });
        }, 1200);
        return;
      }

      this.clearPendingOriginal();
      this.prepareOriginalGalleryHeight(original);

      // Atomic handoff in one sync turn so mobile never paints both stacked.
      this.setVisible(original, true);
      this.setVisible(this.$el, false);
      this.$el.querySelectorAll('[data-ctr-ab-group]').forEach((el) => this.setVisible(el, false));
      this.wasAbActive = false;
      this.activeGroupKey = null;
      this.setPswpTemplateActive(null);
      this.refreshOriginalGalleryHeight(original);
    },

    showAbGroup(groupKey) {
      this.clearPendingOriginal();
      const original = this.getOriginalGallery();
      this.setVisible(original, false);
      this.setVisible(this.$el, true);
      this.wasAbActive = true;

      let activeEl = null;
      this.$el.querySelectorAll('[data-ctr-ab-group]').forEach((el) => {
        const isActive = el.getAttribute('data-ctr-ab-group') === groupKey;
        this.setVisible(el, isActive);
        if (isActive) activeEl = el;
      });

      this.activeGroupKey = groupKey;
      this.setPswpTemplateActive(activeEl);
      this.ensureDots(activeEl);
      this.bindSlideScroll(activeEl);
      this.refreshGalleryHeight(activeEl);
    },

    getZoomHost(groupEl) {
      return groupEl?.querySelector('.ctr-ab-gallery__zoom-host') || null;
    },

    getVisibleSlides(groupEl) {
      if (!groupEl) return [];
      return Array.from(groupEl.querySelectorAll('[data-slide][data-media-id]')).filter(
        (slide) => !slide.classList.contains('ctr-ab-gallery__flavor-hidden')
      );
    },

    isSubscribeSelected() {
      if (!this.sectionEl) return false;
      const input = this.sectionEl.querySelector(
        'input[name="selling_plan"], select[name="selling_plan"]'
      );
      return Boolean(input?.value);
    },

    getPurchaseAttr(el) {
      if (!el) return '';
      return (
        el.getAttribute('data-ctr-ab-purchase') ||
        el.querySelector('[data-thumb-link]')?.getAttribute('data-ctr-ab-purchase') ||
        ''
      );
    },

    flavorHasOtpSlides(groupEl, flavor) {
      if (!groupEl || !flavor) return false;
      return Array.from(groupEl.querySelectorAll('[data-slide]')).some((slide) => {
        if (slide.getAttribute('data-ctr-ab-flavor') !== flavor) return false;
        return this.getPurchaseAttr(slide) !== 'subscribe';
      });
    },

    isVisibleForPurchase(el, flavor, groupEl) {
      if (this.getPurchaseAttr(el) !== 'subscribe') return true;
      if (this.isSubscribeSelected()) return true;
      const group = groupEl || el.closest('[data-ctr-ab-group]');
      return !this.flavorHasOtpSlides(group, flavor);
    },

    isVisibleForFlavor(el, flavor, groupEl) {
      if (!el) return false;
      const slideFlavor =
        el.getAttribute('data-ctr-ab-flavor') ||
        el.querySelector('[data-thumb-link]')?.getAttribute('data-ctr-ab-flavor');
      if (slideFlavor && slideFlavor !== flavor) return false;
      return this.isVisibleForPurchase(el, flavor, groupEl);
    },

    /** Ensure ProductImages built dots (may have skipped init while empty/hidden). */
    ensureDots(groupEl) {
      const host = this.getZoomHost(groupEl);
      if (!host) return;

      const slides = this.getVisibleSlides(groupEl);
      if (slides.length <= 1) {
        host.querySelector('.product__media-dots')?.remove();
        if (host.dotsWrap !== undefined) {
          host.dotsWrap = null;
          host.dots = [];
        }
        return;
      }

      let dotsWrap = host.querySelector('.product__media-dots');
      if (!dotsWrap) {
        dotsWrap = document.createElement('div');
        dotsWrap.className = 'product__media-dots';
        dotsWrap.setAttribute('role', 'tablist');
        const list = host.querySelector('[data-product-media-list]');
        if (list) list.insertAdjacentElement('afterend', dotsWrap);
        else host.appendChild(dotsWrap);
      }

      const existing = new Set(
        Array.from(dotsWrap.querySelectorAll('.product__media-dot')).map((d) =>
          d.getAttribute('data-media-id')
        )
      );
      const needed = slides.map((s) => s.getAttribute('data-media-id')).filter(Boolean);

      // Rebuild if count/order don't match AB slides
      const matches =
        needed.length === existing.size && needed.every((id) => existing.has(id));
      if (!matches) {
        dotsWrap.innerHTML = '';
        needed.forEach((id, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product__media-dot';
          dot.setAttribute('data-media-id', id);
          dot.setAttribute('aria-label', `Go to image ${index + 1}`);
          dotsWrap.appendChild(dot);
        });
      }

      // Keep ProductImages internal refs in sync when available
      if (host.dotsWrap !== undefined) {
        host.dotsWrap = dotsWrap;
        host.dots = Array.from(dotsWrap.querySelectorAll('.product__media-dot'));
      }
    },

    syncDots(groupEl, mediaId) {
      if (!groupEl || !mediaId) return;
      groupEl.querySelectorAll('.product__media-dot').forEach((dot) => {
        const isMatch = dot.getAttribute('data-media-id') === mediaId;
        dot.classList.toggle('is-selected', isMatch);
        dot.setAttribute('aria-current', isMatch ? 'true' : 'false');
      });
    },

    /**
     * Step through flavor-visible slides only, wrapping at the ends.
     * @param {Element} groupEl
     * @param {number} direction - +1 next, -1 previous
     */
    stepVisibleSlide(groupEl, direction) {
      if (!groupEl) return;
      const visible = this.getVisibleSlides(groupEl);
      if (!visible.length) return;

      const currentId = groupEl.getAttribute('data-active-media');
      let currentIdx = visible.findIndex(
        (slide) => slide.getAttribute('data-media-id') === currentId
      );
      if (currentIdx < 0) currentIdx = 0;

      const nextIdx = (currentIdx + direction + visible.length) % visible.length;
      const mediaId = visible[nextIdx]?.getAttribute('data-media-id');
      if (mediaId) this.setActiveSlide(groupEl, mediaId);
    },

    /**
     * Scroll the active thumb into view in .product__thumbs__holder
     * (same idea as product-thumbs.js scrollToThumb).
     */
    scrollActiveThumbIntoView(groupEl) {
      if (!groupEl) return;
      const holder = groupEl.querySelector('[data-thumbs-slider]');
      const activeThumb = holder?.querySelector('[data-thumb-item].is-active');
      if (!holder || !activeThumb) return;

      const scrollLeft = holder.scrollLeft;
      const holderWidth = holder.offsetWidth;
      const thumbLeft = activeThumb.offsetLeft;
      const thumbWidth = activeThumb.offsetWidth;
      const thumbRight = thumbLeft + thumbWidth;
      const viewRight = scrollLeft + holderWidth;

      if (thumbLeft >= scrollLeft && thumbRight <= viewRight) return;

      let left = thumbLeft;
      if (thumbRight > viewRight) {
        left = thumbLeft - holderWidth + thumbWidth;
      }

      holder.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    },

    /**
     * Single source of truth for active AB slide + thumbs + dots.
     * Mobile: smooth horizontal scroll (slide effect, like default mobile slider).
     * Desktop: media--hiding transition (same as ProductImages / default thumbs).
     */
    setActiveSlide(groupEl, mediaId) {
      if (!groupEl || !mediaId) return;

      const targetSlide = groupEl.querySelector(
        `[data-slide][data-media-id="${CSS.escape(mediaId)}"]`
      );
      if (!targetSlide || targetSlide.classList.contains('ctr-ab-gallery__flavor-hidden')) return;

      const host = this.getZoomHost(groupEl);
      const list = host?.querySelector('[data-product-media-list]');
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const current = groupEl.querySelector('[data-slide].media--active');

      const visibleSlides = this.getVisibleSlides(groupEl);

      if (isMobile && list) {
        // Slide effect: scroll track; keep visible flavor slides in the row
        visibleSlides.forEach((slide) => {
          const isMatch = slide === targetSlide;
          slide.classList.toggle('media--active', isMatch);
          slide.classList.toggle('media--hidden', !isMatch);
          slide.classList.remove('media--hiding');
        });
        list.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
      } else if (current === targetSlide) {
        // already active
      } else if (current && !current.classList.contains('ctr-ab-gallery__flavor-hidden')) {
        // Desktop thumbs: same two-phase transition as ProductImages.setActiveMedia
        current.classList.add('media--hiding');
        current.classList.remove('media--active');

        targetSlide.classList.remove('media--hiding', 'media--hidden');
        targetSlide.classList.add('media--active');

        const onEnd = (e) => {
          if (e.target !== current) return;
          if (!current.classList.contains('media--hiding')) return;
          current.classList.remove('media--hiding');
          current.classList.add('media--hidden');
          current.removeEventListener('transitionend', onEnd);
          current.removeEventListener('transitioncancel', onEnd);
        };
        current.addEventListener('transitionend', onEnd);
        current.addEventListener('transitioncancel', onEnd);

        visibleSlides.forEach((slide) => {
          if (slide === targetSlide || slide === current) return;
          slide.classList.remove('media--active', 'media--hiding');
          slide.classList.add('media--hidden');
        });
      } else {
        visibleSlides.forEach((slide) => {
          const isMatch = slide === targetSlide;
          slide.classList.toggle('media--active', isMatch);
          slide.classList.toggle('media--hidden', !isMatch);
          slide.classList.remove('media--hiding');
        });
      }

      groupEl.setAttribute('data-active-media', mediaId);
      host?.setAttribute('data-active-media', mediaId);

      targetSlide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });

      groupEl.querySelectorAll('[data-thumb-item]:not(.ctr-ab-gallery__flavor-hidden)').forEach((thumb) => {
        const thumbLink = thumb.querySelector('[data-thumb-link]');
        const isMatch = thumbLink?.getAttribute('data-media-id') === mediaId;
        thumb.classList.toggle('is-active', !!isMatch);
        if (thumbLink) thumbLink.setAttribute('aria-current', isMatch ? 'true' : 'false');
      });

      this.ensureDots(groupEl);
      this.syncDots(groupEl, mediaId);
      this.refreshGalleryHeight(groupEl);
      this.bindSlideScroll(groupEl);
      this.scrollActiveThumbIntoView(groupEl);
    },

    /** Keep dots in sync when the user swipes the mobile track. */
    bindSlideScroll(groupEl) {
      const list = this.getZoomHost(groupEl)?.querySelector('[data-product-media-list]');
      if (!list || list.dataset.ctrAbScrollBound === '1') return;
      list.dataset.ctrAbScrollBound = '1';

      let raf = null;
      list.addEventListener(
        'scroll',
        () => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = null;
            if (!window.matchMedia('(max-width: 749px)').matches) return;
            const slides = this.getVisibleSlides(groupEl);
            if (!slides.length) return;

            const listRect = list.getBoundingClientRect();
            const center = listRect.left + listRect.width / 2;
            let closest = slides[0];
            let min = Infinity;
            slides.forEach((slide) => {
              const rect = slide.getBoundingClientRect();
              const dist = Math.abs(rect.left + rect.width / 2 - center);
              if (dist < min) {
                min = dist;
                closest = slide;
              }
            });

            const mediaId = closest.getAttribute('data-media-id');
            if (!mediaId || groupEl.getAttribute('data-active-media') === mediaId) {
              this.syncDots(groupEl, mediaId);
              return;
            }

            slides.forEach((slide) => {
              const isMatch = slide === closest;
              slide.classList.toggle('media--active', isMatch);
              slide.classList.toggle('media--hidden', !isMatch);
            });
            groupEl.setAttribute('data-active-media', mediaId);
            this.getZoomHost(groupEl)?.setAttribute('data-active-media', mediaId);
            this.syncDots(groupEl, mediaId);

            groupEl.querySelectorAll('[data-thumb-item]:not(.ctr-ab-gallery__flavor-hidden)').forEach((thumb) => {
              const thumbLink = thumb.querySelector('[data-thumb-link]');
              const isMatch = thumbLink?.getAttribute('data-media-id') === mediaId;
              thumb.classList.toggle('is-active', !!isMatch);
              if (thumbLink) thumbLink.setAttribute('aria-current', isMatch ? 'true' : 'false');
            });
          });
        },
        { passive: true }
      );
    },

    filterPswpTemplate(groupEl, flavor) {
      const tpl =
        groupEl.querySelector('[data-ctr-ab-pswp-template][data-pswp-thumbs-template]') ||
        groupEl.querySelector('[data-ctr-ab-pswp-template]');
      if (!tpl?.content) return;

      // Keep non-matching items out of the template so zoom.js only clones visibles.
      if (!tpl._ctrAbPswpStash) {
        tpl._ctrAbPswpStash = document.createDocumentFragment();
      }

      while (tpl._ctrAbPswpStash.firstChild) {
        tpl.content.appendChild(tpl._ctrAbPswpStash.firstChild);
      }

      Array.from(tpl.content.querySelectorAll('[data-ctr-ab-pswp-item]')).forEach((item) => {
        if (!this.isVisibleForFlavor(item, flavor, groupEl)) {
          tpl._ctrAbPswpStash.appendChild(item);
        }
      });
    },

    activateFlavor(groupEl, flavor) {
      if (!groupEl || !flavor) return;

      groupEl.querySelectorAll('[data-slide]').forEach((slide) => {
        const visible = this.isVisibleForFlavor(slide, flavor, groupEl);
        slide.classList.toggle('ctr-ab-gallery__flavor-hidden', !visible);
        if (!visible) {
          slide.classList.remove('media--active', 'media--hiding');
          slide.classList.add('media--hidden');
        }
      });

      groupEl.querySelectorAll('[data-thumb-item]').forEach((thumb) => {
        const link = thumb.querySelector('[data-thumb-link]');
        const visible = this.isVisibleForFlavor(thumb, flavor, groupEl);
        thumb.classList.toggle('ctr-ab-gallery__flavor-hidden', !visible);
        if (!visible) {
          thumb.classList.remove('is-active');
          if (link) link.setAttribute('aria-current', 'false');
        }
      });

      this.filterPswpTemplate(groupEl, flavor);

      const visibleSlides = this.getVisibleSlides(groupEl);
      const hero = visibleSlides.find((slide) => slide.getAttribute('data-ctr-ab-flavor') === flavor);
      const activeMediaId =
        hero?.getAttribute('data-media-id') ||
        visibleSlides[0]?.getAttribute('data-media-id') ||
        '';

      this.ensureDots(groupEl);
      if (activeMediaId) this.setActiveSlide(groupEl, activeMediaId);
    },

    sync(opts = {}) {
      if (!this.config) return;

      const override = this.getUrlOverride();
      if (override) {
        this.abGroup = override;
      } else {
        const fromGlobal = this.getGlobalGroup();
        if (fromGlobal) this.abGroup = fromGlobal;
      }

      const variant = this.getVariant();
      const useAb =
        this.igReady &&
        variant &&
        this.isBagVariant(variant) &&
        this.abGroup !== 'control' &&
        this.config.groups[this.abGroup];

      if (!useAb) {
        if (this.pendingOriginal && !opts.galleryReady) {
          this.setVisible(this.$el, true);
          this.setVisible(this.getOriginalGallery(), false);
          return;
        }
        this.showOriginal({ galleryReady: opts.galleryReady });
        return;
      }

      this.showAbGroup(this.abGroup);
      const groupEl = this.$el.querySelector(`[data-ctr-ab-group="${this.abGroup}"]`);
      const flavor =
        this.getFlavorKey(variant) ||
        groupEl?.querySelector('[data-slide][data-ctr-ab-flavor]')?.getAttribute('data-ctr-ab-flavor') ||
        'strawberry-lemon';
      this.activateFlavor(groupEl, flavor);
    },

    onThumbClick(event) {
      const link = event.target.closest('[data-thumb-link]');
      if (!link || !this.$el.contains(link)) return;

      event.preventDefault();
      const mediaId = link.getAttribute('data-media-id');
      const groupEl = link.closest('[data-ctr-ab-group]');
      if (!groupEl || !mediaId) return;

      this.setActiveSlide(groupEl, mediaId);
    },

    onDotClick(event) {
      const dot = event.target.closest('.product__media-dot');
      if (!dot || !this.$el.contains(dot)) return;

      // Own the click — ProductImages may scrollToMedia/selectMedia with a
      // [data-media-id] selector that matches the dot button itself.
      event.preventDefault();
      event.stopImmediatePropagation();
      const mediaId = dot.getAttribute('data-media-id');
      const groupEl = dot.closest('[data-ctr-ab-group]');
      if (!groupEl || !mediaId) return;

      this.setActiveSlide(groupEl, mediaId);
    },

    /**
     * Infer +1/-1 from a theme media id relative to the current slide in DOM order.
     * Handles wrap: last → first is next, first → last is previous.
     */
    getStepDirection(groupEl, mediaId) {
      const all = Array.from(groupEl.querySelectorAll('[data-slide][data-media-id]'));
      if (!all.length) return 1;

      const currentId = groupEl.getAttribute('data-active-media');
      const currentAllIdx = all.findIndex(
        (slide) => slide.getAttribute('data-media-id') === currentId
      );
      const targetAllIdx = all.findIndex(
        (slide) => slide.getAttribute('data-media-id') === mediaId
      );
      if (currentAllIdx < 0 || targetAllIdx < 0) return 1;

      if (targetAllIdx === 0 && currentAllIdx === all.length - 1) return 1;
      if (targetAllIdx === all.length - 1 && currentAllIdx === 0) return -1;
      return targetAllIdx > currentAllIdx ? 1 : -1;
    },

    onMediaSelect(event) {
      if (this._syncingMedia) return;
      const mediaId = event.detail?.id;
      const host = event.currentTarget;
      if (!mediaId || !host) return;

      const groupEl = host.closest('[data-ctr-ab-group]');
      if (!groupEl) return;

      const visible = this.getVisibleSlides(groupEl);
      if (!visible.length) return;

      const currentId = groupEl.getAttribute('data-active-media');
      const currentVisibleIdx = visible.findIndex(
        (slide) => slide.getAttribute('data-media-id') === currentId
      );
      const targetVisibleIdx = visible.findIndex(
        (slide) => slide.getAttribute('data-media-id') === mediaId
      );

      const nextIdx =
        currentVisibleIdx < 0 ? 0 : (currentVisibleIdx + 1) % visible.length;
      const prevIdx =
        currentVisibleIdx < 0
          ? 0
          : (currentVisibleIdx - 1 + visible.length) % visible.length;

      // Theme picked a visible adjacent (or current) slide — use it directly.
      if (
        targetVisibleIdx >= 0 &&
        (targetVisibleIdx === currentVisibleIdx ||
          targetVisibleIdx === nextIdx ||
          targetVisibleIdx === prevIdx)
      ) {
        this.setActiveSlide(groupEl, mediaId);
        return;
      }

      // Flavor-hidden or non-adjacent: step visible set in the theme's direction.
      this.stepVisibleSlide(groupEl, this.getStepDirection(groupEl, mediaId));
    },

    onArrowClick(event) {
      const button = event.target.closest('[data-button-prev], [data-button-next]');
      if (!button || !this.$el.contains(button)) return;

      const host = button.closest('.ctr-ab-gallery__zoom-host');
      const groupEl = host?.closest('[data-ctr-ab-group]');
      if (!groupEl) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const direction = button.hasAttribute('data-button-prev') ? -1 : 1;
      this.stepVisibleSlide(groupEl, direction);
    },

    bindEvents() {
      if (!this.sectionEl) return;

      const onChange = () => this.sync();

      // PGV swaps the original gallery async after variant change — re-hide it.
      const onGalleryLoad = (e) => {
        const target = e.target;
        if (!target || !this.sectionEl.contains(target)) return;
        if (!target.matches?.('[data-product-gallery]')) return;
        if (target.closest('[data-ctr-ab-gallery-root]')) return;
        this.sync({ galleryReady: true });
      };

      this.sectionEl.addEventListener('change', (e) => {
        const t = e.target;
        if (!t) return;
        if (t.matches('input[name="id"], input[name="selling_plan"], select[name="selling_plan"]')) {
          onChange();
        }
      });

      document.addEventListener('variant:change', onChange);
      document.addEventListener('product:variant-change', onChange);
      this.sectionEl.addEventListener('theme:variant:change', onChange);
      this.sectionEl.addEventListener('theme:gallery:load', onGalleryLoad);

      this.$el.addEventListener(
        'click',
        (e) => {
          this.onArrowClick(e);
          this.onDotClick(e);
          this.onThumbClick(e);
        },
        true
      );

      // Keep thumbs/dots in sync when ProductImages keyboard/drag change the slide
      this.$el.querySelectorAll('.ctr-ab-gallery__zoom-host').forEach((host) => {
        host.addEventListener('theme:media:select', (e) => this.onMediaSelect(e));
      });

      const idInput = this.sectionEl.querySelector('input[name="id"]');
      if (idInput) {
        const idObserver = new MutationObserver(onChange);
        idObserver.observe(idInput, { attributes: true, attributeFilter: ['value'] });
      }

      const galleryHost =
        this.$el.parentElement ||
        this.sectionEl.querySelector('.pdp-gallery-desktop') ||
        this.sectionEl;
      if (galleryHost) {
        const galleryObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (!(node instanceof Element)) continue;
              const gallery =
                node.matches?.('[data-product-gallery]') &&
                !node.hasAttribute('data-ctr-ab-group') &&
                !node.closest('[data-ctr-ab-gallery-root]')
                  ? node
                  : null;
              if (!gallery) continue;

              // Keep the incoming gallery hidden until handoff if AB is covering.
              if (this.pendingOriginal || (!this.$el.hidden && this.$el.style.display !== 'none')) {
                gallery.hidden = true;
                gallery.style.display = 'none';
              }
              this.sync({ galleryReady: true });
              return;
            }
          }
        });
        galleryObserver.observe(galleryHost, { childList: true, subtree: false });
      }
    },
  }));
});
